import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '@/services/api'

// Переменные окружения для управления доступом к медиа и memberId по умолчанию
const ENV_MEMBER_ID = process.env.VUE_APP_MEMBER_ID || ''

export const usePortalStore = defineStore('portal', () => {
  const portalData = ref({
    portal: { id: '', domain: '', memberId: ENV_MEMBER_ID },
    memberId: '',
    refresh: '',
    user: null,
    isAdmin: false,
    isViewFiles: true,
    allUsers: [],
    info: {}
  })

  // Кеш пользователей по отделам (deptId -> users[])
  const departmentUsersCache = ref({})
  // Флаги загрузки по отделам
  const departmentUsersLoading = ref({})

  const setPortalData = (data) => {
    // Сливаем данные и аккуратно прокидываем memberId в portal.memberId
    const next = {
      ...portalData.value,
      ...data,
      portal: {
        ...portalData.value.portal,
        ...(data.portal || {}),
      },
    }
    const incomingMemberId = (data.portal && data.portal.memberId) || data.memberId || ''
    if (incomingMemberId) {
      next.memberId = next.memberId || incomingMemberId
      next.portal.memberId = next.portal.memberId || incomingMemberId
    }
    // Если ничего не пришло, но есть ENV, используем его
    if (!next.portal.memberId && ENV_MEMBER_ID) next.portal.memberId = ENV_MEMBER_ID
    portalData.value = next
  }

  const setUser = (userData) => {
    portalData.value.user = userData
  }

  const setIsAdmin = (isAdmin) => {
    portalData.value.isAdmin = isAdmin
    // Всегда разрешаем просмотр/загрузку файлов
    portalData.value.isViewFiles = true
  }

  const setAllUsers = (users) => {
    portalData.value.allUsers = users
  }

  // Получить пользователей отдела из кеша или загрузить
  const getDepartmentUsers = async (deptId) => {
    const id = String(deptId)
    // Если уже есть в кеше — возвращаем
    if (departmentUsersCache.value[id]) {
      return departmentUsersCache.value[id]
    }
    // Если уже загружается — ждём
    if (departmentUsersLoading.value[id]) {
      return new Promise((resolve) => {
        const check = setInterval(() => {
          if (!departmentUsersLoading.value[id]) {
            clearInterval(check)
            resolve(departmentUsersCache.value[id] || [])
          }
        }, 50)
      })
    }
    // Загружаем
    departmentUsersLoading.value[id] = true
    try {
      const list = await api.getBitrixDepartmentUsers(id)
      departmentUsersCache.value[id] = list
      return list
    } catch {
      departmentUsersCache.value[id] = []
      return []
    } finally {
      departmentUsersLoading.value[id] = false
    }
  }

  // Проверить, загружен ли отдел
  const isDepartmentLoaded = (deptId) => {
    return Boolean(departmentUsersCache.value[String(deptId)])
  }

  // Проверить, загружается ли отдел
  const isDepartmentLoading = (deptId) => {
    return Boolean(departmentUsersLoading.value[String(deptId)])
  }

  // Получить кешированных пользователей (без загрузки)
  const getCachedDepartmentUsers = (deptId) => {
    return departmentUsersCache.value[String(deptId)] || null
  }

  // Очистить кеш (например, при смене портала)
  const clearDepartmentUsersCache = () => {
    departmentUsersCache.value = {}
    departmentUsersLoading.value = {}
  }

  return {
    portalData,
    setPortalData,
    setUser,
    setIsAdmin,
    setAllUsers,
    // Кеш пользователей по отделам
    departmentUsersCache,
    departmentUsersLoading,
    getDepartmentUsers,
    isDepartmentLoaded,
    isDepartmentLoading,
    getCachedDepartmentUsers,
    clearDepartmentUsersCache
  }
})
